// ─────────────────────────────────────────────────────────────────────────
// inspection-draft.js
// User-scoped, multi-draft localStorage persistence for the inspection flow.
//
// Schema v2: one draft per (userId × stationId × inspectionType) tuple.
// Stored under a single localStorage key as a JSON map for atomic writes.
//
// Why a server-side draft is NOT used: submitting an inspection has heavy
// immediate side-effects (OL creation, asset status flips, notifications,
// audit log) wired into POST /api/inspections. localStorage gives us safe,
// side-effect-free recovery on the same device.
// ─────────────────────────────────────────────────────────────────────────

const DRAFTS_KEY = 'inspection_drafts_v2';          // map: { "<userId>:<stationId>:<type>": draftDoc }
const LEGACY_KEY = 'inspection_draft_v1';           // pre-v2 single-slot draft
const SCHEMA_VERSION = 2;
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;       // 24h staleness gate
const DRAFT_CHANGE_EVENT = 'inspection-draft-changed';

export { DRAFTS_KEY, LEGACY_KEY, DRAFT_CHANGE_EVENT };

function emitChange() {
  try { window.dispatchEvent(new Event(DRAFT_CHANGE_EVENT)); } catch (e) { /* SSR / no-window */ }
}

function readMap() {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
  } catch (e) {
    return {};
  }
}

function writeMap(map) {
  try {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(map));
    emitChange();
  } catch (e) {
    console.warn('[InspectionDraft] write failed', e);
  }
}

export function draftKey(userId, stationId, inspectionType) {
  return `${userId || 'anon'}:${stationId || 'none'}:${inspectionType || 'individual'}`;
}

// ── One-time migration of any v1 single-slot draft under the current user ──
// Called by the presence hook with the resolved current userId.
export function migrateLegacyIfAny(currentUserId) {
  if (!currentUserId) return;
  let raw;
  try { raw = localStorage.getItem(LEGACY_KEY); } catch (e) { return; }
  if (!raw) return;
  try {
    const legacy = JSON.parse(raw);
    if (!legacy || legacy.v !== 1) {
      localStorage.removeItem(LEGACY_KEY);
      return;
    }
    const stationId = legacy.selectedStation;
    const type = legacy.inspectionType || 'individual';
    if (!stationId) {
      localStorage.removeItem(LEGACY_KEY);
      return;
    }
    const map = readMap();
    const k = draftKey(currentUserId, stationId, type);
    // Only migrate if there isn't already a newer v2 draft for the same slot.
    if (!map[k] || (map[k].saved_at || 0) < (legacy.saved_at || 0)) {
      map[k] = {
        ...legacy,
        v: SCHEMA_VERSION,
        user_id: currentUserId,
        station_id: stationId,
        inspection_type: type,
        started_at: legacy.started_at || legacy.saved_at || Date.now(),
      };
      writeMap(map);
    }
    localStorage.removeItem(LEGACY_KEY);
  } catch (e) {
    console.warn('[InspectionDraft] legacy migration failed', e);
    try { localStorage.removeItem(LEGACY_KEY); } catch (_) { /* noop */ }
  }
}

// ── Save / load / clear by composite key ──────────────────────────────────
export function saveInspectionDraft(state, { userId } = {}) {
  if (!state || !state.selectedStation || !userId) return;
  const map = readMap();
  const k = draftKey(userId, state.selectedStation, state.inspectionType || 'individual');
  const existing = map[k];
  const payload = {
    v: SCHEMA_VERSION,
    user_id: userId,
    station_id: state.selectedStation,
    inspection_type: state.inspectionType || 'individual',
    started_at: existing?.started_at || Date.now(),
    saved_at: Date.now(),
    ...state,
    submitted_sub_zones: Array.from(state.submittedSubZones || []),
    sig_completed_locs: Array.from(state.sigCompletedLocs || []),
    inspection_date_iso:
      state.inspectionDate instanceof Date
        ? state.inspectionDate.toISOString()
        : state.inspectionDate || null,
  };
  delete payload.submittedSubZones;
  delete payload.sigCompletedLocs;
  delete payload.inspectionDate;
  map[k] = payload;
  writeMap(map);
}

function rehydrate(draft) {
  if (!draft) return null;
  return {
    ...draft,
    submittedSubZones: new Set(draft.submitted_sub_zones || []),
    sigCompletedLocs: new Set(draft.sig_completed_locs || []),
    inspectionDate: draft.inspection_date_iso ? new Date(draft.inspection_date_iso) : new Date(),
    _key: draftKey(draft.user_id, draft.station_id, draft.inspection_type),
  };
}

function isExpired(draft) {
  return !draft?.saved_at || (Date.now() - draft.saved_at) > MAX_DRAFT_AGE_MS;
}

export function loadDraftByKey(key) {
  const map = readMap();
  const d = map[key];
  if (!d) return null;
  if (isExpired(d)) {
    delete map[key];
    writeMap(map);
    return null;
  }
  return rehydrate(d);
}

export function loadDraft({ userId, stationId, inspectionType }) {
  return loadDraftByKey(draftKey(userId, stationId, inspectionType));
}

export function loadUserDrafts(userId) {
  if (!userId) return [];
  const map = readMap();
  const prefix = `${userId}:`;
  const out = [];
  let dirty = false;
  for (const [k, d] of Object.entries(map)) {
    if (!k.startsWith(prefix)) continue;
    if (isExpired(d)) {
      delete map[k];
      dirty = true;
      continue;
    }
    // Also drop drafts that ended up with zero items (orphans).
    if (!d.inspectionItems || d.inspectionItems.length === 0) continue;
    out.push(rehydrate(d));
  }
  if (dirty) writeMap(map);
  // Most recent first.
  out.sort((a, b) => (b.saved_at || 0) - (a.saved_at || 0));
  return out;
}

export function clearDraftByKey(key) {
  const map = readMap();
  if (key in map) {
    delete map[key];
    writeMap(map);
  }
}

export function clearInspectionDraft({ userId, stationId, inspectionType } = {}) {
  if (!userId || !stationId) return;
  clearDraftByKey(draftKey(userId, stationId, inspectionType));
}

export function clearAllUserDrafts(userId) {
  if (!userId) return;
  const map = readMap();
  const prefix = `${userId}:`;
  let dirty = false;
  for (const k of Object.keys(map)) {
    if (k.startsWith(prefix)) { delete map[k]; dirty = true; }
  }
  if (dirty) writeMap(map);
}

// ── Summary helper for compact card surfaces ──────────────────────────────
export function draftSummary(draft) {
  if (!draft) return null;
  const items = draft.inspectionItems || [];
  const savedAgo = Math.max(0, Date.now() - (draft.saved_at || 0));
  const startedAgo = Math.max(0, Date.now() - (draft.started_at || draft.saved_at || 0));
  const fmt = (ms) => {
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} h ago`;
    return `${Math.floor(hr / 24)} d ago`;
  };
  return {
    itemCount: items.length,
    inspectionType: draft.inspection_type || draft.inspectionType || 'individual',
    when: fmt(savedAgo),
    startedWhen: fmt(startedAgo),
    stationId: draft.station_id || draft.selectedStation || null,
    userId: draft.user_id || null,
    key: draft._key || draftKey(draft.user_id, draft.station_id || draft.selectedStation, draft.inspection_type || draft.inspectionType),
    progress: draft.progress || null,
  };
}
